import { TruncatedTextReadMore } from "@/components/ui/TruncatedTextReadMore";
import { CourseSkillLevelBadge } from "@/components/ui/CourseSkillLevelBadge";
import { BRAND_COLORS } from "@/lib/brandColors";
import { CATALOG_STALE_TIME } from "@/lib/queryClient";
import { CourseSkillLevel } from "@/lib/constants";
import { useIsMobile } from "@/lib/useDeviceType";
import {
  getInstructorsByCourseId,
  type Instructor,
} from "@/supabase/services/instructors";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import { Image, XStack, YStack } from "tamagui";
import { PageTitle, SubtitleText } from "@/components/ui/Typography";

// Keep in sync with InstructorHero so both drill-in heroes match.
const AVATAR_DESKTOP = 128;
const AVATAR_MOBILE = 88;

type BannerHeroProps = {
  courseId?: string | number;
  courseTitle?: string | null;
  courseSummary?: string | null;
  courseAvatarUrl?: string | null;
  courseSkillLevel?: CourseSkillLevel | null;
};

export function BannerHero({
  courseId,
  courseTitle: courseTitleProp,
  courseSummary: courseSummaryProp,
  courseAvatarUrl,
  courseSkillLevel,
}: BannerHeroProps) {
  const isMobile = useIsMobile();

  const parsedCourseId =
    typeof courseId === "string" ? Number.parseInt(courseId, 10) : courseId;

  const avatarSize = isMobile ? AVATAR_MOBILE : AVATAR_DESKTOP;

  const instructorsQuery = useQuery({
    queryKey: ["course", parsedCourseId, "instructors"],
    queryFn: () => getInstructorsByCourseId(parsedCourseId!),
    staleTime: CATALOG_STALE_TIME,
    enabled:
      typeof parsedCourseId === "number" && !Number.isNaN(parsedCourseId),
  });

  const instructors: Instructor[] = instructorsQuery.data ?? [];
  const instructorNames = instructors.map((i) => i.name).join(" & ");

  const courseTitle = courseTitleProp?.trim() ? courseTitleProp : null;
  const subtitle =
    courseSummaryProp && String(courseSummaryProp).trim()
      ? String(courseSummaryProp).trim()
      : null;

  if (!courseTitle) {
    return null;
  }

  const avatarUri = courseAvatarUrl?.trim() || null;

  const summaryNode = subtitle ? (
    <TruncatedTextReadMore
      numberOfLines={isMobile ? 3 : 2}
      textProps={{
        ff: "$body",
        fontWeight: "400",
        size: "$3",
        letterSpacing: 0,
        color: BRAND_COLORS.text,
        $gtMd: { size: "$4" },
      }}
    >
      {subtitle}
    </TruncatedTextReadMore>
  ) : null;

  return (
    <YStack w="100%" maw={1140} als="center" px={16} pt={32} $gtSm={{ pt: 40 }}>
      <XStack
        minHeight={avatarSize}
        ai="flex-start"
        jc="flex-start"
        gap={11}
        $gtSm={{ gap: 20 }}
      >
        {avatarUri ? (
          <Image
            source={{ uri: avatarUri }}
            w={avatarSize}
            h={avatarSize}
            br={avatarSize / 2}
            resizeMode="cover"
            flexShrink={0}
            borderWidth={1}
            borderColor="#E0DDD8"
          />
        ) : (
          <YStack
            w={avatarSize}
            h={avatarSize}
            br={avatarSize / 2}
            backgroundColor="#D4CFC4"
            flexShrink={0}
            borderWidth={1}
            borderColor="#E0DDD8"
          />
        )}

        <YStack f={1} miw={0} jc="flex-start">
          <XStack ai="center" jc="flex-start" gap={10} $gtSm={{ gap: 16 }}>
            <PageTitle
              color={BRAND_COLORS.text}
              flexGrow={0}
              flexShrink={1}
              minWidth={0}
            >
              {courseTitle}
            </PageTitle>
            {courseSkillLevel != null ? (
              <CourseSkillLevelBadge
                level={courseSkillLevel}
                showLabel={!isMobile}
              />
            ) : null}
          </XStack>
          {instructorNames ? (
            <SubtitleText
              color={BRAND_COLORS.instructorMeta}
              mt="$1.5"
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {instructorNames}
            </SubtitleText>
          ) : null}
          {/* Desktop/web: summary sits beside the avatar, in the text column. */}
          {!isMobile && summaryNode ? (
            <YStack mt="$1" width="100%">
              {summaryNode}
            </YStack>
          ) : null}
        </YStack>
      </XStack>

      {/* Mobile: summary spans full width below, using the space under the avatar. */}
      {isMobile && summaryNode ? (
        <YStack mt="$3" width="100%">
          {summaryNode}
        </YStack>
      ) : null}
    </YStack>
  );
}
